/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Tabs } from '@base-ui/react/tabs';

import { DatePickerActions } from './DatePickerActions';
import { DatesPanel } from './DatesPanel';
import { FlexiblePanel } from './FlexiblePanel';
import type {
    DatePickerCloseAction,
    DatePickerState,
    DatePickerVariant,
} from './DatePicker.types';
import {
    getApplyClickTag,
    getClearButtonTracking,
    getTabClickTagAction,
    trackClick,
} from './DatePicker.tracking';

import styles from './DatePickerContent.module.scss';

interface DatePickerContentProps {
    isFlexibleAllowed: boolean;
    state: DatePickerState;
    setState: React.Dispatch<React.SetStateAction<DatePickerState>>;
    onClearDates: ({ commit }: { commit?: boolean }) => void;
    hasDates: boolean;
    /** What closing would do to the committed filters. Sets the primary button's label. */
    closeAction: DatePickerCloseAction;
    /** Called by the primary action. The wrapper should close itself, which commits the draft. */
    onApply: () => void;
    variant?: DatePickerVariant;
    /** GA event category. Omit to send no click events. */
    clickTagCategory?: string;
}

/**
 * Body of the date picker, independent of the popover or drawer that wraps it.
 */
export function DatePickerContent({
    isFlexibleAllowed,
    state,
    setState,
    onClearDates,
    hasDates,
    closeAction,
    onApply,
    variant = 'popover',
    clickTagCategory,
}: DatePickerContentProps) {
    return (
        <div
            className={cx(styles.PopupContent, styles.DatesPopupContent, {
                [styles.isPopover]: variant === 'popover',
                [styles.isDrawer]: variant === 'drawer',
            })}
        >
            <div className={styles.Body}>
                {isFlexibleAllowed ? (
                    <Tabs.Root
                        className={styles.TabsRoot}
                        value={state.activeTab}
                        onValueChange={(value) => {
                            const activeTab = value as DatePickerState['activeTab'];
                            trackClick(clickTagCategory, getTabClickTagAction(activeTab));

                            setState((prev) => {
                                return {
                                    ...prev,
                                    activeTab,
                                };
                            });
                        }}
                    >
                        <div className={styles.TabsHeader}>
                            <Tabs.List className={styles.TabsList}>
                                <Tabs.Tab value="dates" className={styles.TabsTab}>
                                    Choose Dates
                                </Tabs.Tab>
                                <Tabs.Tab value="flexible" className={styles.TabsTab}>
                                    I‘m Flexible
                                </Tabs.Tab>
                                <Tabs.Indicator className={styles.TabsIndicator} />
                            </Tabs.List>
                        </div>
                        <Tabs.Panel
                            value="dates"
                            className={styles.TabsPanel}
                            tabIndex={-1}
                        >
                            <DatesPanel
                                state={state}
                                setState={setState}
                                isFlexibleAllowed={isFlexibleAllowed}
                                variant={variant}
                                isInTab={true}
                            />
                        </Tabs.Panel>
                        <Tabs.Panel
                            value="flexible"
                            className={cx(styles.TabsPanel, styles.FlexibleTabsPanel)}
                            tabIndex={-1}
                        >
                            <FlexiblePanel
                                state={state}
                                setState={setState}
                                variant={variant}
                                clickTagCategory={clickTagCategory}
                            />
                        </Tabs.Panel>
                    </Tabs.Root>
                ) : (
                    <DatesPanel
                        state={state}
                        setState={setState}
                        isFlexibleAllowed={isFlexibleAllowed}
                        variant={variant}
                    />
                )}
            </div>

            <DatePickerActions
                closeAction={closeAction}
                onReset={() => onClearDates({ commit: false })}
                isResetDisabled={!hasDates}
                onApply={onApply}
                variant={variant}
                resetTracking={getClearButtonTracking(
                    state.activeTab,
                    clickTagCategory,
                    variant === 'drawer'
                )}
                applyTracking={getApplyClickTag(state, clickTagCategory)}
            />
        </div>
    );
}
